import React, { Ref, memo, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useDrag, useDrop } from 'react-dnd'

import { CustomTag } from '@modules/custom-tags/types'
import DeleteIcon from '@mui/icons-material/DeleteOutline'
import DragIndicatorIcon from '@mui/icons-material/DragIndicator'
import { Box, Typography } from '@mui/material'

import TagColorPicker from '../TagColorPicker'
import { CustomTagNameWrapper, CustomTagRowWrapper, EditCustomTagTextField, TagDescriptionWrapper } from '../index.styles'

export interface CustomTagRowProps {
  tag: CustomTag
  existingTags: CustomTag[]
  onDelete: () => void
  onTagUpdate: ({ color, name }: { color?: string; name?: string }) => void
  onOrderChange: (tagIndex: number, newTagOrder: number) => void
  shouldEdit: boolean
}

const CustomTagRow = ({ tag, existingTags, onDelete, onOrderChange, onTagUpdate, shouldEdit }: CustomTagRowProps) => {
  const dropRef = useRef(null)

  const [{ isDragging }, drag] = useDrag(() => ({
    type: 'custom-tag',
    item: { index: existingTags.findIndex(existingTag => existingTag === tag) },
    collect: monitor => ({
      isDragging: monitor.isDragging(),
    }),
  }))

  const handleDropCallback = useCallback(
    (item: { index: number }) => {
      onOrderChange(item.index, tag.order)
    },
    [tag, onOrderChange],
  )

  const [collectedProps, drop] = useDrop(
    () => ({
      accept: 'custom-tag',
      drop: handleDropCallback,
      collect: monitor => {
        const isOver = monitor.isOver()
        let dropPosition = 'unknown'

        if (isOver) {
          const clientOffset = monitor.getClientOffset()
          if (clientOffset && dropRef.current) {
            // @ts-expect-error temp
            const rect = dropRef.current.getBoundingClientRect()
            const dropTargetHeight = rect.bottom - rect.top
            const dropTargetYMiddle = rect.top + dropTargetHeight / 2
            const dropY = clientOffset.y

            dropPosition = dropY < dropTargetYMiddle ? 'top' : 'bottom'
          }
        }

        return { isOver, dropPosition, draggedItem: monitor.getItem() }
      },
    }),
    [tag, existingTags],
  )

  const draggedItem = useMemo(
    () => existingTags.find((i, idx) => idx === collectedProps.draggedItem?.index),
    [collectedProps.draggedItem?.index, existingTags],
  )

  const [isInEditMode, setIsInEditMode] = useState(false)

  drop(dropRef)

  useEffect(() => {
    setIsInEditMode(shouldEdit)
  }, [shouldEdit])

  return (
    <Box display="flex" flexDirection="column" ref={dropRef}>
      {collectedProps.dropPosition === 'top' && !!draggedItem && (
        <CustomTagRowWrapper $grey>
          <TagDescriptionWrapper>
            <DragIndicatorIcon />
            <TagColorPicker tag={draggedItem} existingTags={existingTags} onColorSelected={color => onTagUpdate({ color })} />
            <CustomTagNameWrapper>
              <Typography variant="body1">{draggedItem.name}</Typography>
            </CustomTagNameWrapper>
          </TagDescriptionWrapper>
          <DeleteIcon sx={{ cursor: 'pointer' }} color="primary" />
        </CustomTagRowWrapper>
      )}

      <CustomTagRowWrapper $isDragged={isDragging}>
        <TagDescriptionWrapper ref={drag as unknown as Ref<HTMLDivElement>}>
          <DragIndicatorIcon sx={{ cursor: 'grab' }} />
          <TagColorPicker tag={tag} existingTags={existingTags} onColorSelected={color => onTagUpdate({ color })} />
          {isInEditMode ? (
            <EditCustomTagTextField
              sx={{ width: '100%', maxHeight: '25px' }}
              autoFocus
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => onTagUpdate({ name: e.target?.value as string })}
              onBlur={(e: React.FocusEvent<HTMLInputElement>) => {
                setIsInEditMode(false)
                onTagUpdate({ name: e.target?.value as string })
              }}
              value={tag.name}
            />
          ) : (
            <CustomTagNameWrapper onClick={() => setIsInEditMode?.(true)}>
              <Typography variant="body1">{tag.name}</Typography>
            </CustomTagNameWrapper>
          )}
        </TagDescriptionWrapper>
        <DeleteIcon sx={{ cursor: 'pointer' }} onClick={() => onDelete()} color="primary" />
      </CustomTagRowWrapper>

      {collectedProps.dropPosition === 'bottom' && !!draggedItem && (
        <CustomTagRowWrapper $grey>
          <TagDescriptionWrapper>
            <DragIndicatorIcon />
            <TagColorPicker tag={draggedItem} existingTags={existingTags} onColorSelected={color => onTagUpdate({ color })} />
            <CustomTagNameWrapper>
              <Typography variant="body1">{draggedItem.name}</Typography>
            </CustomTagNameWrapper>
          </TagDescriptionWrapper>
          <DeleteIcon sx={{ cursor: 'pointer' }} color="primary" />
        </CustomTagRowWrapper>
      )}
    </Box>
  )
}

export default memo(CustomTagRow)
